import { ElementType, createElement, useMemo } from 'react';

import { InboxIcon, OctagonAlertIcon, Trash2Icon, ZapIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { MessagesCircleIcon } from '@/components/icons/MessagesCircleIcon';
import { Department } from '@/modules/departments/models';
import { getPermissions } from '@/redux/permissions/selectors';
import { CountsKeys, AgentCounts, DepartmentCounts } from '@/shared/types/chat';
import { actions } from '@/shared/types/permissions';
import { Presence } from '@/shared/types/presence';
import { RootState } from '@/shared/types/state';
import { numberFormatter } from '@/util/numbers';

import { AgentIcon } from '../components/AgentIcon/AgentIcon';
import { CircleUserRoundDotted } from '../icons/CircleUserRoundDottedIcon';
import { MenuItem, NavNotificationKey } from '../models';

export type MenuType = MenuItem[];

interface Props {
  counts?: Record<CountsKeys, number>;
  agentCounts?: AgentCounts;
  agents?: Presence[];
  departments?: Department[];
  departmentCounts?: DepartmentCounts;
  userDepartments?: readonly Department[];
  onPinClick?: (key: string) => void;
  onItemClick?: (key: string) => void;
}

const iconResolver: Record<CountsKeys & 'brain', ElementType> = {
  inbox: createElement(InboxIcon, { size: 16 }),
  all: createElement(MessagesCircleIcon, { size: 16 }),
  unassigned: createElement(CircleUserRoundDotted, { size: 16 }),
  spam: createElement(OctagonAlertIcon, { size: 16 }),
  trash: createElement(Trash2Icon, { size: 16 }),
  brain: createElement(ZapIcon, { size: 16 }),
};

const toNotificationKey = (key: string): NavNotificationKey | undefined =>
  key === 'inbox' || key === 'unassigned' ? key : undefined;

// Exact count for the hover tooltip — only when the compact label actually rounds (differs from raw).
const getExactSum = (value: number, language: string): string | undefined => {
  const exact = value.toLocaleString(language);
  return `${numberFormatter(value)}` === exact ? undefined : exact;
};

export const useNavMenu = ({
  counts,
  agentCounts,
  agents,
  departments,
  departmentCounts,
  userDepartments,
  onPinClick,
  onItemClick,
}: Props) => {
  const { t, i18n } = useTranslation();

  const canReadOthers = useSelector((state: RootState) =>
    getPermissions(state, 'conversations', actions.READ_OTHERS)
  );

  const canReadUnassigned = useSelector((state: RootState) =>
    getPermissions(state, 'conversations', actions.READ_UNASSIGNED)
  );

  const canReadBrain = useSelector((state: RootState) =>
    getPermissions(state, 'conversations', actions.READ_BRAIN)
  );

  const canReadDepartmentsOthers = useSelector((state: RootState) =>
    getPermissions(state, 'conversations.read.departments', actions.OTHERS)
  );

  const mainMenu = useMemo(() => {
    if (!counts) {
      return [];
    }
    // Hide inboxes a restricted agent can't use (backend enforces the reads;
    // `all` collapses to own-only and `unassigned` is 0 for them).
    const tabVisibility: Partial<Record<string, boolean>> = {
      all: canReadOthers,
      unassigned: canReadUnassigned,
    };
    return Object.keys(counts)
      .filter((key) => tabVisibility[key] ?? true)
      .map((key) => {
        const adjustedKey = key === 'inbox' ? 'me' : key;
        return {
          text: t(`conversation.${adjustedKey}`),
          icon: iconResolver[key],
          onClick: () => onItemClick(adjustedKey),
          id: key,
          sum: `${numberFormatter(counts[key])}`,
          exactSum: getExactSum(counts[key], i18n.language),
          notificationKey: toNotificationKey(key),
        };
      });
  }, [counts, canReadOthers, canReadUnassigned, t, i18n.language, onItemClick]);

  const brainCount = agentCounts?.brain || { open: 0 };

  const openByAgent = useMemo(
    () => new Map((agentCounts?.agents ?? []).map((item) => [item.agent_id, item.open])),
    [agentCounts?.agents]
  );

  const openByDepartment = useMemo(
    () =>
      new Map((departmentCounts?.departments ?? []).map((item) => [item.department_id, item.open])),
    [departmentCounts?.departments]
  );

  const userDepartmentIds = useMemo(
    () => new Set((userDepartments ?? []).map(({ department_id }) => department_id)),
    [userDepartments]
  );

  const agentMenu = useMemo(() => {
    if (!agents) {
      return [];
    }
    const assistant = {
      text: t('conversation.assistant'),
      icon: iconResolver['brain'],
      onClick: () => onItemClick('brain'),
      onPinClick: () => onPinClick('brain'),
      id: 'brain',
      sum: `${numberFormatter(brainCount.open)}`,
      exactSum: getExactSum(brainCount.open, i18n.language),
      pinned: true,
    };
    // Not `toSorted`: browserslist goes back to Chrome 107, and nothing polyfills it.
    const sortedAgents = agents
      .filter((agent) => canReadBrain || agent?.name !== 'brain')
      .sort((a, b) =>
        (a?.name ?? '').localeCompare(b?.name ?? '', undefined, { sensitivity: 'base' })
      );
    return [
      ...(canReadBrain ? [assistant] : []),
      ...sortedAgents.map((agent) => {
        const isBrainAgent = agent?.name === 'brain';
        const openCount = openByAgent.get(agent?.agent_id) ?? 0;

        return {
          text: isBrainAgent ? t('conversation.assistant') : agent?.name,
          icon: () => createElement(AgentIcon, { agent }),
          onClick: () => onItemClick(agent?.agent_id),
          onPinClick: () => onPinClick(agent?.agent_id),
          id: agent?.agent_id,
          sum: `${numberFormatter(openCount)}`,
          exactSum: getExactSum(openCount, i18n.language),
          disabled: !canReadOthers,
        };
      }),
    ];
  }, [
    t,
    i18n.language,
    brainCount.open,
    agents,
    onItemClick,
    onPinClick,
    openByAgent,
    canReadOthers,
    canReadBrain,
  ]);

  const departmentsMenu = useMemo(() => {
    if (!departments) {
      return [];
    }
    return departments.map((department) => {
      const openCount = openByDepartment.get(department?.department_id);
      const isUserDepartment = userDepartmentIds.has(department?.department_id);

      return {
        text: department?.name,
        onClick: () => onItemClick(department?.department_id),
        onPinClick: () => onPinClick(department?.department_id),
        id: department?.department_id,
        sum: openCount === undefined ? null : `${numberFormatter(openCount)}`,
        exactSum: openCount === undefined ? undefined : getExactSum(openCount, i18n.language),
        icon: () => createElement('span', { width: 16, height: 16 }, department?.icon),
        disabled: !canReadDepartmentsOthers && !isUserDepartment,
        pinned: isUserDepartment,
      };
    });
  }, [
    canReadDepartmentsOthers,
    openByDepartment,
    departments,
    onItemClick,
    onPinClick,
    userDepartmentIds,
    i18n.language,
  ]);

  return {
    mainMenu,
    agentMenu,
    departmentsMenu,
    canReadOthers,
    canReadBrain,
    canReadDepartmentsOthers,
  };
};
